/**
 * Modal that prompts an authorized user (pharmacist / admin) to enter
 * their initials + password to approve a restricted action for the
 * requesting user.
 *
 * On success it returns a signed approval token that the calling
 * component attaches to the actual API request.
 *
 * @example
 * ```tsx
 * <AuthorizationApprovalModal
 *   open={showApproval}
 *   onClose={() => setShowApproval(false)}
 *   onApproved={(token, approver) => {
 *     // token is the signed approval token
 *     // approver has { name, role, initials }
 *     submitDispense({ ...data, approvalToken: token })
 *   }}
 *   permission="dispenses:create"
 *   actionDescription="Create a dispense"
 *   eligibleApprovers={[
 *     { name: 'Jane Smith', initials: 'JS', role: 'pharmacist' },
 *   ]}
 * />
 * ```
 */

import * as React from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  Button,
  Label,
  Alert,
  AlertDescription,
  Input,
  Badge,
} from "@repo/ui";
import { Loader2, ShieldAlert } from "lucide-react";
import type { Permission, PermissionContext, Role } from "@/lib/permissions";
import { getRoleDisplayName } from "@/lib/permissions";
import type { EligibleApprover } from "@/hooks/use-approval-handler";
import { authorizeAction } from "@/lib/api/auth";
import { ApiClientError } from "@/lib/api/client";

// -------------------------------------------------------
// Types
// -------------------------------------------------------

interface ApproverInfo {
  name: string;
  role: string;
  initials: string;
}

interface AuthorizationApprovalModalProps {
  open: boolean;
  onClose: () => void;
  /**
   * Called when authorization succeeds.
   * @param approvalToken — signed token to send with the action request
   * @param approver — info about the approving user
   */
  onApproved: (approvalToken: string, approver: ApproverInfo) => void;
  /** The permission key being authorized. */
  permission: Permission;
  /** Optional context for ABAC checks (drug schedule, etc.). */
  context?: PermissionContext;
  /** Human-readable description of the action being authorized. */
  actionDescription?: string;
  /** Which roles can approve (used as fallback when eligibleApprovers is empty). */
  approverRoles?: Role[];
  /** Actual users who can approve this action. */
  eligibleApprovers?: EligibleApprover[];
}

// -------------------------------------------------------
// Component
// -------------------------------------------------------

export function AuthorizationApprovalModal({
  open,
  onClose,
  onApproved,
  permission,
  context,
  actionDescription,
  approverRoles,
  eligibleApprovers,
}: AuthorizationApprovalModalProps) {
  const [initials, setInitials] = React.useState("");
  const [password, setPassword] = React.useState("");
  const [error, setError] = React.useState("");
  const [isLoading, setIsLoading] = React.useState(false);

  const handleClose = () => {
    setInitials("");
    setPassword("");
    setError("");
    setIsLoading(false);
    onClose();
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");

    if (!initials || initials.length < 2) {
      setError("Please enter the approver's initials (at least 2 characters)");
      return;
    }
    if (!password || password.length < 4) {
      setError("Please enter the approver's password");
      return;
    }

    setIsLoading(true);

    try {
      const data = await authorizeAction({
        approverInitials: initials,
        approverPassword: password,
        permission,
        context: context ?? undefined,
      });

      // Success — pass token + approver info to parent
      onApproved(data.approvalToken, data.approver);
      handleClose();
    } catch (error) {
      if (error instanceof ApiClientError) {
        setError(error.message || "Authorization failed");
      } else {
        setError("An unexpected error occurred");
      }
      setIsLoading(false);
    }
  };

  const hasApprovers = eligibleApprovers && eligibleApprovers.length > 0;

  // Fallback role badges when no eligible approvers are provided
  const displayRoles =
    approverRoles ??
    (["admin", "supervising_pharmacist", "pharmacist"] as Role[]);

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <div className="flex items-center gap-2">
            <ShieldAlert className="h-5 w-5 text-amber-500" />
            <DialogTitle>Authorization Required</DialogTitle>
          </div>
          <DialogDescription>
            {actionDescription
              ? `This action requires authorization: ${actionDescription}`
              : "This action requires authorization from a qualified user."}
          </DialogDescription>
        </DialogHeader>

        {/* Show eligible approvers by name, or fall back to role badges */}
        {hasApprovers ? (
          <div className="space-y-1.5">
            <span className="text-sm text-muted-foreground">
              Ask one of these users to authorize:
            </span>
            <div className="flex flex-wrap gap-1.5">
              {eligibleApprovers.map((approver) => (
                <Badge
                  key={approver.initials}
                  variant="secondary"
                  className="text-xs"
                >
                  {approver.name}
                </Badge>
              ))}
            </div>
          </div>
        ) : (
          <div className="flex flex-wrap items-center gap-1.5">
            <span className="text-sm text-muted-foreground">
              Authorized roles:
            </span>
            {displayRoles.map((role) => (
              <Badge key={role} variant="secondary" className="text-xs">
                {getRoleDisplayName(role)}
              </Badge>
            ))}
          </div>
        )}

        {error && (
          <Alert variant="destructive">
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        <form onSubmit={handleSubmit} className="space-y-4" autoComplete="off">
          {/* Initials */}
          <div className="space-y-2">
            <Label htmlFor="approver-initials">
              Initials <span className="text-red-500">*</span>
            </Label>
            <Input
              id="approver-initials"
              type="text"
              placeholder="Enter approver's initials"
              value={initials}
              onChange={(e) => setInitials(e.target.value.toUpperCase())}
              maxLength={5}
              autoFocus
              disabled={isLoading}
              className={error ? "border-red-500" : ""}
              autoComplete="off"
              data-testid="dispense-approval-initials"
            />
          </div>

          {/* Password */}
          <div className="space-y-2">
            <Label htmlFor="approver-password">
              Password <span className="text-red-500">*</span>
            </Label>
            <Input
              id="approver-password"
              type="password"
              placeholder="Enter approver's password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              disabled={isLoading}
              className={error ? "border-red-500" : ""}
              autoComplete="one-time-code"
              data-testid="dispense-approval-password"
            />
          </div>

          <div className="flex justify-end gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={handleClose}
              disabled={isLoading}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={isLoading}
              data-testid="dispense-approval-submit"
            >
              {isLoading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Verifying...
                </>
              ) : (
                "Authorize"
              )}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
